// Phone Validation Service
// Uses Twilio Lookup API to validate phone numbers and check if they're SMS-capable
// This prevents sending SMS to landlines, fax numbers, etc.

import { TWILIO_CONFIG } from '../config/twilioConfig';

export interface PhoneValidationResult {
  isValid: boolean;
  isSmsCapable: boolean;
  phoneNumber: string;
  nationalFormat?: string;
  countryCode?: string;
  carrierName?: string;
  carrierType?: 'mobile' | 'landline' | 'voip' | 'unknown';
  error?: string;
}

class PhoneValidationService {
  private readonly TWILIO_LOOKUP_URL = 'https://lookups.twilio.com/v1/PhoneNumbers';
  
  /**
   * Validate phone number and check if it's SMS-capable
   * Uses Twilio Lookup API with carrier information
   */
  async validatePhoneNumber(phoneNumber: string): Promise<PhoneValidationResult> {
    try {
      // Format phone number first
      const formattedNumber = this.formatPhoneNumber(phoneNumber);
      
      // In development/testing, we might not have Twilio credentials
      // So we'll do basic validation and return a warning
      if (this.isDevelopmentMode()) {
        return this.validateInDevelopmentMode(formattedNumber);
      }
      
      // In production, use Twilio Lookup API
      return await this.lookupWithTwilio(formattedNumber);
    } catch (error: any) {
      console.error('Error validating phone number:', error);
      return {
        isValid: false,
        isSmsCapable: false,
        phoneNumber,
        error: error.message || 'Validation failed',
      };
    }
  }
  
  /**
   * Lookup phone number using Twilio Lookup API
   */
  private async lookupWithTwilio(phoneNumber: string): Promise<PhoneValidationResult> {
    try {
      // Get Twilio credentials
      const accountSid = TWILIO_CONFIG.accountSid;
      const authToken = TWILIO_CONFIG.authToken;
      
      if (!accountSid || !authToken) {
        console.warn('⚠️ Twilio credentials not configured, using basic validation');
        return this.validateInDevelopmentMode(phoneNumber);
      }
      
      // Make request to Twilio Lookup API
      const lookupUrl = `${this.TWILIO_LOOKUP_URL}/${encodeURIComponent(phoneNumber)}?Type=carrier`;
      const auth = Buffer.from(`${accountSid}:${authToken}`).toString('base64');
      
      const response = await fetch(lookupUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Lookup failed: ${response.status}`);
      }
      
      const lookup = await response.json();
      
      // Extract carrier information
      const carrierType = this.parseCarrierType(lookup.carrier?.type);
      const isSmsCapable = this.isSmsCapableType(carrierType);
      
      return {
        isValid: true,
        isSmsCapable,
        phoneNumber: lookup.phone_number || phoneNumber,
        nationalFormat: lookup.national_format,
        countryCode: lookup.country_code,
        carrierName: lookup.carrier?.name,
        carrierType,
      };
    } catch (error: any) {
      console.error('Twilio lookup error:', error);
      
      // If lookup fails, return basic validation
      return {
        isValid: this.isValidPhoneFormat(phoneNumber),
        isSmsCapable: false, // Unknown, assume not SMS-capable to be safe
        phoneNumber,
        error: error.message || 'Lookup failed',
      };
    }
  }
  
  /**
   * Development mode validation (when Twilio not available)
   * Does basic format checking but can't verify SMS capability
   */
  private validateInDevelopmentMode(phoneNumber: string): PhoneValidationResult {
    const isValid = this.isValidPhoneFormat(phoneNumber);
    
    return {
      isValid,
      isSmsCapable: false, // Unknown in dev mode - will need to test
      phoneNumber,
      carrierType: 'unknown',
      error: isValid 
        ? 'Twilio credentials not configured - cannot verify SMS capability'
        : 'Invalid phone number format',
    };
  }
  
  /**
   * Check if phone number format is valid
   */
  private isValidPhoneFormat(phone: string): boolean {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // US/Canada: 10 digits (without country code) or 11 digits (with country code)
    if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
      return true;
    }
    
    // International: Check if it starts with + and has reasonable length
    if (phone.startsWith('+') && cleaned.length >= 7 && cleaned.length <= 15) {
      return true;
    }
    
    return false;
  }
  
  /**
   * Format phone number to E.164 format
   */
  private formatPhoneNumber(phone: string): string {
    // Remove all non-numeric characters except +
    const cleaned = phone.replace(/[^\d+]/g, '');
    
    // If already in E.164 format (starts with +), return as is
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // Remove leading + if present for processing
    const digits = cleaned.replace(/^\+/, '');
    
    // US/Canada: Add +1 if 10 digits
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    
    // If 11 digits and starts with 1, add +
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    
    // Otherwise, try to add + if not present
    return cleaned.startsWith('+') ? cleaned : `+${digits}`;
  }
  
  /**
   * Parse carrier type from Twilio response
   */
  private parseCarrierType(type?: string): 'mobile' | 'landline' | 'voip' | 'unknown' {
    if (!type) return 'unknown';
    
    const lowerType = type.toLowerCase();
    
    if (lowerType.includes('mobile') || lowerType.includes('cell')) {
      return 'mobile';
    }
    
    if (lowerType.includes('landline') || lowerType.includes('fixed')) {
      return 'landline';
    }
    
    if (lowerType.includes('voip')) {
      return 'voip';
    }
    
    return 'unknown';
  }
  
  /**
   * Check if carrier type supports SMS
   */
  private isSmsCapableType(carrierType: 'mobile' | 'landline' | 'voip' | 'unknown'): boolean {
    // Mobile and VoIP typically support SMS
    // Landlines do not support SMS
    return carrierType === 'mobile' || carrierType === 'voip';
  }
  
  /**
   * Check if we're in development mode
   */
  private isDevelopmentMode(): boolean {
    return (
      process.env.NODE_ENV === 'development' ||
      !TWILIO_CONFIG?.accountSid ||
      !TWILIO_CONFIG?.authToken
    );
  }
  
  /**
   * Quick check: Is phone number likely SMS-capable?
   * Uses heuristics when Twilio lookup is not available
   */
  async quickCheck(phoneNumber: string): Promise<boolean> {
    try {
      const result = await this.validatePhoneNumber(phoneNumber);
      return result.isSmsCapable;
    } catch (error) {
      // If validation fails, assume not SMS-capable to be safe
      return false;
    }
  }
  
  /**
   * Get user-friendly error message for non-SMS-capable numbers
   */
  getErrorMessage(result: PhoneValidationResult): string {
    if (!result.isValid) {
      return 'Invalid phone number format. Please check the number and try again.';
    }
    
    if (!result.isSmsCapable) {
      if (result.carrierType === 'landline') {
        return 'This appears to be a landline number. SMS messages can only be sent to mobile numbers. Please provide a mobile phone number or contact the business directly.';
      }
      
      return 'This phone number may not support SMS messages. Please provide a mobile phone number or contact the business directly.';
    }
    
    return '';
  }
}

export const phoneValidationService = new PhoneValidationService();
export default phoneValidationService;






